declare global {
  interface Window {
    ym?: (id: number, action: string, ...args: unknown[]) => void
    yandex_metrika_callbacks?: Array<() => void>
  }
}

let metrikaId: number | null = null

/**
 * Initialise Yandex Metrika. Pass an empty string to keep analytics silent
 * (development / pre-launch). Actual tag injection is intentionally omitted
 * here — wire the script tag via index.html or GTM when ready.
 */
export function init(id: string): void {
  const parsed = parseInt(id, 10)
  if (!id || isNaN(parsed)) {
    metrikaId = null
    return
  }
  metrikaId = parsed
}

/**
 * Send a goal event. No-op when metrikaId is not initialised.
 */
export function reachGoal(name: string, params?: Record<string, unknown>): void {
  if (metrikaId === null) {
    return
  }
  if (typeof window !== 'undefined' && typeof window.ym === 'function') {
    try {
      window.ym(metrikaId, 'reachGoal', name, params)
    } catch {
      // Analytics must never interrupt the product flow.
    }
  }
}

/**
 * Send a pageview for client-side navigation. No-op when metrikaId is not
 * initialised or Metrika is blocked.
 */
export function hit(url: string): void {
  if (metrikaId === null) {
    return
  }
  if (typeof window !== 'undefined' && typeof window.ym === 'function') {
    try {
      window.ym(metrikaId, 'hit', sanitizePageUrl(url))
    } catch {
      // Analytics must never interrupt client-side navigation.
    }
  }
}

/**
 * Keep query parameters and fragments out of analytics pageviews. Utility
 * share links may contain user-provided dates, times, and zones.
 */
export function sanitizePageUrl(url: string): string {
  const fallbackOrigin = 'https://analytics.invalid'
  const parsed = new URL(url, fallbackOrigin)
  return parsed.origin === fallbackOrigin
    ? parsed.pathname
    : `${parsed.origin}${parsed.pathname}`
}
